/**
 * Report analytics — design tokens, formatters and chart option factories.
 *
 * One module so every analytics panel draws from the same palette and the same
 * axis/tooltip language; a second report adding its own colors is how a
 * dashboard stops reading as one system.
 *
 * PALETTE
 * -------
 * The three series hues are a validated categorical set: worst adjacent CVD
 * ΔE 9.2 and normal-vision ΔE 27.6 on a white surface (OKLab ×100 — the
 * thresholds are ≥8 and ≥15). Slot 1 is the product blue, so charts read as
 * part of the app rather than as a widget bolted on. Aqua sits below 3:1
 * against white, which is legal only because every value it encodes is also
 * directly labelled AND available in the Table tab — do not use it for a mark
 * that carries meaning on its own.
 *
 * Status colors (good/critical) are reserved for SLA outcomes and never used
 * as a fourth series: a color that sometimes means "breached" and sometimes
 * means "category 4" means nothing.
 *
 * Chrome (grid, axis, ink) is the app's cool gray scale rather than the
 * palette's warm neutrals, so the charts sit in the existing pages.
 */

export const VIZ = {
  surface: '#FFFFFF',
  ink: '#0f172a',
  inkSoft: '#52607A',
  muted: '#8A94A6',
  grid: '#EEF1F5',
  axis: '#D8DEE6',
  /** Categorical slots, in fixed order — never cycled, never reassigned by rank. */
  series: ['#3366FF', '#EB6834', '#1BAF7A'] as const,
  /** Light step of the series-1 hue: bar-list tracks and meter troughs. */
  track: '#EDF2FF',
  status: {
    good: '#0CA30C',
    warning: '#FAB219',
    critical: '#D03B3B',
  },
} as const

/**
 * Sequential ramp — one hue, light→dark, for continuous magnitude (the
 * heatmap). Never a rainbow: a multi-hue ramp makes the reader learn an
 * arbitrary color order before they can read a value.
 */
export const SEQUENTIAL = ['#EDF2FF', '#CDE2FB', '#9EC5F4', '#6DA7EC', '#3987E5', '#256ABF', '#184F95'] as const

/** `#3366FF` + 0.1 → `rgba(51, 102, 255, 0.1)`. Area washes only. */
export function withAlpha(hex: string, alpha: number): string {
  const value = hex.replace('#', '')
  const r = parseInt(value.slice(0, 2), 16)
  const g = parseInt(value.slice(2, 4), 16)
  const b = parseInt(value.slice(4, 6), 16)
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

// ---------------------------------------------------------------------------
// Formatters
// ---------------------------------------------------------------------------

/** `1284` → `1,284`; `12934` → `12.9K`. Compact only past 10K, so ordinary counts stay exact. */
export function formatCount(value: number): string {
  if (!Number.isFinite(value)) return '—'
  const abs = Math.abs(value)
  if (abs >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`
  if (abs >= 10_000) return `${(value / 1000).toFixed(1)}K`
  return Math.round(value).toLocaleString()
}

/** `9015` → `2h 30m`. Seconds are dropped once the value runs to hours. */
export function formatSeconds(seconds: number | null): string {
  if (seconds === null || !Number.isFinite(seconds)) return '—'
  const total = Math.max(0, Math.round(seconds))
  if (total === 0) return '0s'
  const h = Math.floor(total / 3600)
  const m = Math.floor((total % 3600) / 60)
  const s = total % 60
  if (h > 0) return m > 0 ? `${h}h ${m}m` : `${h}h`
  if (m > 0) return s > 0 ? `${m}m ${s}s` : `${m}m`
  return `${s}s`
}

export function formatPct(value: number | null, digits = 0): string {
  if (value === null || !Number.isFinite(value)) return '—'
  return `${value.toFixed(digits)}%`
}

/** ISO timestamp → `2026-08-14`, in the browser's zone. Null for unparseable input. */
export function dayKey(value: unknown): string | null {
  if (typeof value !== 'string' || value === '') return null
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return null
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

/** `2026-08-14` → `14 Aug`. Axis ticks only — the tooltip carries the full date. */
export function shortDay(key: string): string {
  const date = new Date(`${key}T00:00:00`)
  if (Number.isNaN(date.getTime())) return key
  return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short' })
}

// ---------------------------------------------------------------------------
// Aggregation
// ---------------------------------------------------------------------------

export function sumBy<T>(rows: readonly T[], value: (row: T) => number): number {
  let total = 0
  for (const row of rows) total += value(row) || 0
  return total
}

/** Mean of the non-null values; null when nothing is measurable (never 0). */
export function meanOf(values: readonly (number | null)[]): number | null {
  const present = values.filter((v): v is number => v !== null && Number.isFinite(v))
  if (present.length === 0) return null
  return present.reduce((a, b) => a + b, 0) / present.length
}

/**
 * Mean weighted by each value's own sample count — the only correct way to
 * combine per-agent averages. A plain mean of means lets an agent who handled
 * two conversations move the org-wide number as much as one who handled 200.
 */
export function weightedMean(pairs: readonly (readonly [number | null, number])[]): number | null {
  let weighted = 0
  let weight = 0
  for (const [value, w] of pairs) {
    if (value === null || !Number.isFinite(value) || w <= 0) continue
    weighted += value * w
    weight += w
  }
  if (weight === 0) return meanOf(pairs.map(([v]) => v))
  return weighted / weight
}

export function ratePct(part: number, whole: number): number | null {
  if (whole <= 0) return null
  return (part / whole) * 100
}

export interface Slice {
  label: string
  value: number
}

/**
 * Count rows per key, sorted high→low, with the tail folded into "Other".
 *
 * Folding rather than adding hues past the top N is deliberate: a generated
 * ninth color is indistinguishable from an existing one under CVD.
 */
export function tally(
  rows: readonly Record<string, unknown>[],
  key: string,
  options: { limit?: number; emptyLabel?: string } = {},
): Slice[] {
  const limit = options.limit ?? 8
  const emptyLabel = options.emptyLabel ?? 'Unspecified'
  const counts = new Map<string, number>()
  for (const row of rows) {
    const raw = row[key]
    const label =
      raw === null || raw === undefined || String(raw).trim() === ''
        ? emptyLabel
        : String(raw).trim()
    counts.set(label, (counts.get(label) ?? 0) + 1)
  }
  const sorted = [...counts.entries()]
    .map(([label, value]) => ({ label, value }))
    .sort((a, b) => b.value - a.value)
  if (sorted.length <= limit) return sorted
  const head = sorted.slice(0, limit)
  const tail = sorted.slice(limit)
  head.push({ label: `Other (${tail.length})`, value: tail.reduce((a, b) => a + b.value, 0) })
  return head
}

/** Rows bucketed by calendar day, gap-filled so a quiet day reads as zero, not as absent. */
export function dailySeries(
  rows: readonly Record<string, unknown>[],
  key: string,
): { labels: string[]; values: number[] } {
  const counts = new Map<string, number>()
  for (const row of rows) {
    const day = dayKey(row[key])
    if (day === null) continue
    counts.set(day, (counts.get(day) ?? 0) + 1)
  }
  if (counts.size === 0) return { labels: [], values: [] }

  const days = [...counts.keys()].sort()
  const first = days[0]
  const last = days[days.length - 1]
  if (first === undefined || last === undefined) return { labels: [], values: [] }

  const labels: string[] = []
  const values: number[] = []
  const cursor = new Date(`${first}T00:00:00`)
  const end = new Date(`${last}T00:00:00`)
  // Bounded by the filter drawer's date range, so this can't run away.
  while (cursor.getTime() <= end.getTime()) {
    const y = cursor.getFullYear()
    const m = String(cursor.getMonth() + 1).padStart(2, '0')
    const d = String(cursor.getDate()).padStart(2, '0')
    const key2 = `${y}-${m}-${d}`
    labels.push(key2)
    values.push(counts.get(key2) ?? 0)
    cursor.setDate(cursor.getDate() + 1)
  }
  return { labels, values }
}

/**
 * Volume by weekday × hour.
 *
 * Returns a 7×24 matrix (row 0 = Monday) plus the peak cell, so the panel can
 * both draw the grid and say in words where the load actually lands — the
 * finding a heatmap exists to surface.
 */
export interface HourDayMatrix {
  matrix: number[][]
  max: number
  total: number
  peak: { day: number; hour: number; value: number } | null
}

export const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const

export function hourDayMatrix(
  rows: readonly Record<string, unknown>[],
  key: string,
): HourDayMatrix {
  const matrix: number[][] = Array.from({ length: 7 }, () => Array.from({ length: 24 }, () => 0))
  let max = 0
  let total = 0
  let peak: { day: number; hour: number; value: number } | null = null

  for (const row of rows) {
    const raw = row[key]
    if (typeof raw !== 'string' || raw === '') continue
    const date = new Date(raw)
    if (Number.isNaN(date.getTime())) continue
    // JS weeks start on Sunday; the grid starts on Monday, which is how a
    // contact centre reads its week.
    const day = (date.getDay() + 6) % 7
    const hour = date.getHours()
    const dayRow = matrix[day]
    if (!dayRow) continue
    const next = (dayRow[hour] ?? 0) + 1
    dayRow[hour] = next
    total += 1
    if (next > max) {
      max = next
      peak = { day, hour, value: next }
    }
  }
  return { matrix, max, total, peak }
}

/** `14` → `2 PM`. Heatmap axis labels only. */
export function hourLabel(hour: number): string {
  if (hour === 0) return '12 AM'
  if (hour === 12) return '12 PM'
  return hour < 12 ? `${hour} AM` : `${hour - 12} PM`
}

export interface Bucket {
  label: string
  value: number
}

/**
 * Distribution over fixed duration bands rather than equal-width bins.
 *
 * Handling times are heavily right-skewed — a handful of week-old outliers
 * would flatten equal-width bins into one tall bar and six empty ones. Bands
 * people actually think in ("under an hour", "1–4h") keep the shape readable.
 */
const DURATION_BANDS: { label: string; maxSeconds: number }[] = [
  { label: '< 5m', maxSeconds: 300 },
  { label: '5–30m', maxSeconds: 1800 },
  { label: '30m–1h', maxSeconds: 3600 },
  { label: '1–4h', maxSeconds: 14400 },
  { label: '4–8h', maxSeconds: 28800 },
  { label: '8–24h', maxSeconds: 86400 },
  { label: '1–3d', maxSeconds: 259200 },
]

export function durationBuckets(values: readonly (number | null)[]): Bucket[] {
  const buckets: Bucket[] = [
    ...DURATION_BANDS.map((b) => ({ label: b.label, value: 0 })),
    { label: '> 3d', value: 0 },
  ]
  for (const value of values) {
    if (value === null || !Number.isFinite(value) || value < 0) continue
    const index = DURATION_BANDS.findIndex((b) => value < b.maxSeconds)
    const target = buckets[index === -1 ? buckets.length - 1 : index]
    if (target) target.value += 1
  }
  return buckets
}

/**
 * Median — reported beside the mean because one abandoned conversation left
 * open over a weekend can move an average by hours while the typical case is
 * unchanged.
 */
export function medianOf(values: readonly (number | null)[]): number | null {
  const present = values
    .filter((v): v is number => v !== null && Number.isFinite(v))
    .sort((a, b) => a - b)
  if (present.length === 0) return null
  const mid = Math.floor(present.length / 2)
  if (present.length % 2 === 1) return present[mid] ?? null
  const lower = present[mid - 1]
  const upper = present[mid]
  if (lower === undefined || upper === undefined) return null
  return (lower + upper) / 2
}

/** Breach rate per dimension value — the "where is the SLA actually going" cut. */
export interface BreachRow {
  label: string
  total: number
  breached: number
  pct: number
}

export function breachBy(
  rows: readonly Record<string, unknown>[],
  key: string,
  flag: string,
  options: { limit?: number; minSample?: number } = {},
): BreachRow[] {
  const limit = options.limit ?? 6
  const minSample = options.minSample ?? 1
  const totals = new Map<string, { total: number; breached: number }>()
  for (const row of rows) {
    const raw = row[flag]
    // Only rows the flag was evaluated on can contribute to a rate.
    if (raw === null || raw === undefined || String(raw).trim() === '') continue
    const label =
      row[key] === null || row[key] === undefined || String(row[key]).trim() === ''
        ? 'Unspecified'
        : String(row[key]).trim()
    const entry = totals.get(label) ?? { total: 0, breached: 0 }
    entry.total += 1
    if (raw === true || raw === 'true') entry.breached += 1
    totals.set(label, entry)
  }
  return [...totals.entries()]
    .filter(([, v]) => v.total >= minSample)
    .map(([label, v]) => ({
      label,
      total: v.total,
      breached: v.breached,
      pct: (v.breached / v.total) * 100,
    }))
    .sort((a, b) => b.pct - a.pct || b.total - a.total)
    .slice(0, limit)
}

// ---------------------------------------------------------------------------
// Chart.js options
//
// Shared so every canvas chart carries the same recessive grid, the same
// tooltip, and the same mark specs: bars capped at 24px with a rounded data-end
// and a square baseline, 2px lines, 8px markers ringed in the surface color.
// PrimeReact's <Chart> takes plain objects, so these stay untyped by chart.js.
// ---------------------------------------------------------------------------

interface TooltipContext {
  dataset: { label?: string }
  parsed: { x?: number; y?: number }
  label?: string
}

type ValueFormat = (value: number) => string

function tooltipBlock(format: ValueFormat, horizontal: boolean): Record<string, unknown> {
  return {
    backgroundColor: VIZ.ink,
    titleColor: '#FFFFFF',
    bodyColor: '#DCE3EF',
    borderWidth: 0,
    padding: 10,
    cornerRadius: 4,
    boxWidth: 8,
    boxHeight: 8,
    boxPadding: 4,
    usePointStyle: true,
    titleFont: { size: 12, weight: '600' },
    bodyFont: { size: 12 },
    callbacks: {
      label: (ctx: TooltipContext): string => {
        const raw = horizontal ? ctx.parsed.x : ctx.parsed.y
        const value = typeof raw === 'number' ? format(raw) : '—'
        const name = ctx.dataset.label
        return name ? ` ${name}: ${value}` : ` ${value}`
      },
    },
  }
}

function legendBlock(show: boolean): Record<string, unknown> {
  return {
    display: show,
    position: 'top',
    align: 'end',
    labels: {
      usePointStyle: true,
      pointStyle: 'circle',
      boxWidth: 8,
      boxHeight: 8,
      padding: 16,
      color: VIZ.inkSoft,
      font: { size: 11.5, weight: '600' },
    },
  }
}

const TICK_FONT = { size: 11 }

export interface BarOptionsInput {
  /** Bars grow left→right. Use it whenever category names are long. */
  horizontal?: boolean
  stacked?: boolean
  /** Legend is suppressed for a single series — the card title already names it. */
  legend?: boolean
  format?: ValueFormat
  /** Hard cap for a percentage axis, so 100% is the end of the track. */
  max?: number
}

export function barOptions(input: BarOptionsInput = {}): Record<string, unknown> {
  const horizontal = input.horizontal ?? false
  const format = input.format ?? formatCount
  const valueAxis = {
    stacked: input.stacked ?? false,
    beginAtZero: true,
    ...(input.max !== undefined ? { max: input.max } : {}),
    border: { display: false },
    grid: { color: VIZ.grid, drawTicks: false, lineWidth: 1 },
    ticks: {
      color: VIZ.muted,
      font: TICK_FONT,
      padding: 6,
      maxTicksLimit: 6,
      callback: (value: string | number): string =>
        format(typeof value === 'number' ? value : Number(value)),
    },
  }
  const categoryAxis = {
    stacked: input.stacked ?? false,
    border: { display: false },
    grid: { display: false },
    ticks: { color: VIZ.inkSoft, font: TICK_FONT, padding: 8, autoSkip: false },
  }

  return {
    indexAxis: horizontal ? 'y' : 'x',
    maintainAspectRatio: false,
    responsive: true,
    layout: { padding: { top: 4, right: 8, bottom: 0, left: 0 } },
    interaction: { mode: 'index', intersect: false },
    scales: horizontal
      ? { x: valueAxis, y: categoryAxis }
      : { x: categoryAxis, y: valueAxis },
    plugins: {
      legend: legendBlock(input.legend ?? false),
      tooltip: tooltipBlock(format, horizontal),
    },
  }
}

/** Bar dataset defaults: ≤24px thick, rounded data-end, square baseline. */
export function barDataset(
  label: string,
  data: readonly number[],
  color: string,
  options: { stacked?: boolean } = {},
): Record<string, unknown> {
  return {
    label,
    data: [...data],
    backgroundColor: color,
    hoverBackgroundColor: color,
    maxBarThickness: 24,
    borderRadius: 4,
    // Leaves the baseline edge square; the growing end is the rounded one.
    borderSkipped: 'start',
    // A stack's segments are separated by a 2px gap in the surface color —
    // white doing the separating, rather than an outline drawn on the mark.
    ...(options.stacked ? { borderColor: VIZ.surface, borderWidth: 2 } : {}),
  }
}

export interface LineOptionsInput {
  legend?: boolean
  format?: ValueFormat
  /** Axis ticks for the category axis (dates are stored as keys, shown short). */
  tickLabel?: (raw: string) => string
}

export function lineOptions(input: LineOptionsInput = {}): Record<string, unknown> {
  const format = input.format ?? formatCount
  return {
    maintainAspectRatio: false,
    responsive: true,
    layout: { padding: { top: 8, right: 12, bottom: 0, left: 0 } },
    interaction: { mode: 'index', intersect: false },
    scales: {
      x: {
        border: { display: false },
        grid: { display: false },
        ticks: {
          color: VIZ.muted,
          font: TICK_FONT,
          padding: 8,
          maxRotation: 0,
          autoSkipPadding: 24,
          ...(input.tickLabel
            ? {
                callback: function tick(this: { getLabelForValue: (v: number) => string }, value: number): string {
                  return input.tickLabel?.(this.getLabelForValue(value)) ?? String(value)
                },
              }
            : {}),
        },
      },
      y: {
        beginAtZero: true,
        border: { display: false },
        grid: { color: VIZ.grid, drawTicks: false, lineWidth: 1 },
        ticks: {
          color: VIZ.muted,
          font: TICK_FONT,
          padding: 6,
          maxTicksLimit: 5,
          callback: (value: string | number): string =>
            format(typeof value === 'number' ? value : Number(value)),
        },
      },
    },
    plugins: {
      legend: legendBlock(input.legend ?? false),
      tooltip: tooltipBlock(format, false),
    },
  }
}

/**
 * Scatter options — one series, so identity is carried by the axes and the
 * tooltip rather than by hue. Used for "volume vs attainment", where the shape
 * of the cloud (does SLA fall as load rises?) is the whole point.
 */
export function scatterOptions(input: {
  xLabel: string
  yLabel: string
  xFormat?: ValueFormat
  yFormat?: ValueFormat
  yMax?: number
  pointLabel: (index: number) => string
}): Record<string, unknown> {
  const xFormat = input.xFormat ?? formatCount
  const yFormat = input.yFormat ?? formatCount
  const axisTitle = (text: string): Record<string, unknown> => ({
    display: true,
    text,
    color: VIZ.muted,
    font: { size: 11, weight: '600' },
    padding: { top: 6, bottom: 0 },
  })

  return {
    maintainAspectRatio: false,
    responsive: true,
    layout: { padding: { top: 8, right: 12, bottom: 0, left: 0 } },
    scales: {
      x: {
        beginAtZero: true,
        title: axisTitle(input.xLabel),
        border: { display: false },
        grid: { color: VIZ.grid, drawTicks: false, lineWidth: 1 },
        ticks: {
          color: VIZ.muted,
          font: TICK_FONT,
          padding: 6,
          maxTicksLimit: 6,
          callback: (v: string | number): string => xFormat(typeof v === 'number' ? v : Number(v)),
        },
      },
      y: {
        beginAtZero: true,
        ...(input.yMax !== undefined ? { max: input.yMax } : {}),
        title: axisTitle(input.yLabel),
        border: { display: false },
        grid: { color: VIZ.grid, drawTicks: false, lineWidth: 1 },
        ticks: {
          color: VIZ.muted,
          font: TICK_FONT,
          padding: 6,
          maxTicksLimit: 5,
          callback: (v: string | number): string => yFormat(typeof v === 'number' ? v : Number(v)),
        },
      },
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: VIZ.ink,
        titleColor: '#FFFFFF',
        bodyColor: '#DCE3EF',
        padding: 10,
        cornerRadius: 4,
        displayColors: false,
        titleFont: { size: 12, weight: '600' },
        bodyFont: { size: 12 },
        callbacks: {
          title: (items: { dataIndex: number }[]): string =>
            input.pointLabel(items[0]?.dataIndex ?? 0),
          label: (ctx: TooltipContext & { dataIndex: number }): string[] => [
            ` ${input.xLabel}: ${xFormat(ctx.parsed.x ?? 0)}`,
            ` ${input.yLabel}: ${yFormat(ctx.parsed.y ?? 0)}`,
          ],
        },
      },
    },
  }
}

/** Scatter dataset: 8px markers ringed in the surface color, one hue. */
export function scatterDataset(
  label: string,
  points: readonly { x: number; y: number }[],
  color: string,
): Record<string, unknown> {
  return {
    label,
    data: points.map((p) => ({ x: p.x, y: p.y })),
    backgroundColor: withAlpha(color, 0.75),
    borderColor: VIZ.surface,
    borderWidth: 2,
    pointRadius: 6,
    pointHoverRadius: 8,
  }
}

/** Line dataset: 2px stroke, 10% wash, markers ringed in the surface color. */
export function lineDataset(
  label: string,
  data: readonly number[],
  color: string,
  options: { fill?: boolean } = {},
): Record<string, unknown> {
  const dense = data.length > 24
  return {
    label,
    data: [...data],
    borderColor: color,
    borderWidth: 2,
    tension: 0.32,
    fill: options.fill ?? false,
    backgroundColor: withAlpha(color, 0.1),
    // Markers are hidden once points crowd; hover still surfaces one.
    pointRadius: dense ? 0 : 4,
    pointHoverRadius: 5,
    pointBackgroundColor: color,
    pointBorderColor: VIZ.surface,
    pointBorderWidth: 2,
    pointHoverBorderWidth: 2,
  }
}
